/**
 * AVBP — Recoverable Trash: portal seam (2026-05-22, owner decision).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  Owner decision: deleted leads / clients / projects / partners go to a
 *  recoverable Trash, NOT a hard delete.
 *
 *    · Move to Trash / Restore — ordinary client-SDK writes. Implemented in
 *      `leadStore.ts` (trashLeadDoc / restoreLeadDoc) and `projectStore.ts`
 *      (trashProject / trashClient / trashPartner — with a trashed flag).
 *    · Permanent purge — CANNOT be a client-SDK write (`firestore.rules`
 *      has `allow delete: if false` for these collections, project-global,
 *      shared with the live HR PWA — never loosened). It is the admin-only
 *      `purgeTrashedRecord` / `emptyTrash` Cloud Function (admin SDK
 *      bypasses the rule). This module is the thin callable seam over it.
 *      Partners were added to the purge scope 2026-05-22 (owner decision —
 *      consistent with leads / clients / projects).
 *
 *  This file also holds the capability predicates + the unified `TrashItem`
 *  model the Trash view renders.
 *
 *  PURE / IMPURE split: `canManageTrash`, `canPurgeTrash`, `canMergeClients`
 *  are pure + unit-tested. `purgeTrashedRecord` / `emptyTrash` are the thin
 *  callable seam.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { Role } from './roles';

/* ════════════════════ Capability resolution (PURE) ════════════════════
 *
 * Capability keys (registered in staffRoles.ts AVBP_PERMISSIONS, the
 * capability-framework lock):
 *   trash.manage   — move to / restore from Trash + view the Trash.
 *                    DEFAULT admin; the owner may later grant it to the
 *                    Admin Assistant role via Settings → Roles & Rights.
 *   trash.purge    — permanent delete (Empty Trash). admin-only, NOT
 *                    grantable.
 *   clients.merge  — merge a duplicate client into another. admin-only.
 *
 * NOTE: AVBP today gates the UI on the coarse LOGIN role (there is no live
 * granular per-capability grant surface yet — `permissionsForRoles` derives
 * capabilities FROM the role). So all three predicates resolve to
 * `role === 'admin'` for now. When the granular grant system lands these
 * predicates take a capability set instead — the call sites do not change.
 */

/** Who may move to / restore from Trash + open the Trash view. */
export function canManageTrash(role: Role | null | undefined): boolean {
  return role === 'admin';
}

/** Who may PERMANENTLY delete from Trash (irreversible). Admin only. */
export function canPurgeTrash(role: Role | null | undefined): boolean {
  return role === 'admin';
}

/** Who may merge a duplicate client into another. Admin only. */
export function canMergeClients(role: Role | null | undefined): boolean {
  return role === 'admin';
}

/* ════════════════════ Unified Trash item model ════════════════════ */

/**
 * AD-038 — `property` is a Trash kind (whole-property soft-delete + soft-deleted
 * nested concepts/versions/floors). Unlike the other four, a property's permanent
 * purge is NOT yet wired through the purge CF (its `TRASHABLE` allowlist is
 * leads/clients/projects/partners; widening it + a `properties` rule is an off-
 * lane functions-repo change — deferred, see AD-038 BUILT note). Nested-item
 * purge IS available now (it's a client-SDK array update, not a doc delete).
 *
 * `family` (Group soft-delete, 2026-07-06) is likewise RESTORE-ONLY here: a
 * trashed Group is recoverable via `restoreFamily`, but permanent purge is not
 * wired — the purge CF `TRASHABLE` allowlist (leads/clients/projects/partners)
 * is an off-lane functions-repo change, deferred (Vex). So `family` is absent
 * from `TRASH_COLLECTION` / the purge seam, exactly like `property`.
 */
export type TrashKind = 'lead' | 'client' | 'project' | 'partner' | 'property' | 'family';

/** The collection a Trash kind maps onto (the CF `collection` argument).
 *  `property` is intentionally absent from the purge CF (see TrashKind note). */
export const TRASH_COLLECTION: Record<'lead' | 'client' | 'project' | 'partner', string> = {
  lead: 'leads',
  client: 'clients',
  project: 'projects',
  partner: 'partners',
};

/* ════════════════════ Callable seam (impure) ════════════════════ */

export interface PurgeInput {
  collection: 'leads' | 'clients' | 'projects' | 'partners';
  docId: string;
}

export interface PurgeResult {
  ok: boolean;
  collection: string;
  docId: string;
}

/**
 * Permanently delete ONE trashed record. The Cloud Function (admin SDK)
 * verifies the record is `trashed:true` first and REFUSES otherwise — a
 * live record can never be destroyed by this call. Throws on permission /
 * not-found / not-trashed.
 */
export async function purgeTrashedRecord(
  input: PurgeInput,
): Promise<PurgeResult> {
  if (!input.docId) throw new Error('No record selected.');
  const fn = httpsCallable<PurgeInput, PurgeResult>(
    functions,
    'purgeTrashedRecord',
  );
  const res = await fn(input);
  return res.data;
}

/** Purge one record identified by its TrashKind (UI-friendly wrapper).
 *  `property` is rejected here — it has no purge-CF collection (see TrashKind). */
export async function purgeTrashItem(
  kind: 'lead' | 'client' | 'project' | 'partner',
  docId: string,
): Promise<PurgeResult> {
  return purgeTrashedRecord({
    collection: TRASH_COLLECTION[kind] as PurgeInput['collection'],
    docId,
  });
}

export interface EmptyTrashInput {
  /** Which collections to empty. Omit → all four. */
  collections?: ('leads' | 'clients' | 'projects' | 'partners')[];
}

export interface EmptyTrashResult {
  ok: boolean;
  deletedByCollection: Record<string, number>;
  totalDeleted: number;
  /** true → the per-call cap was hit; run Empty Trash again to finish. */
  more: boolean;
}

/**
 * Permanently delete EVERY trashed record (optionally scoped to specific
 * collections). The CF verifies each doc is trashed before deleting and is
 * bounded per call (`more:true` → run again). Throws on permission error.
 */
export async function emptyTrash(
  input: EmptyTrashInput = {},
): Promise<EmptyTrashResult> {
  const fn = httpsCallable<EmptyTrashInput, EmptyTrashResult>(
    functions,
    'emptyTrash',
  );
  const res = await fn(input);
  return res.data;
}
